<template>
  <Dropdown
      ref="dropdownRef"
      class="input-group"
      :popper-class="['v-popper--select', {'v-popper--select-multiple': multiple}, popperClass]"
      :class="[inputGroupClass]"
      auto-size
      auto-boundary-max-size
      :placement="placement"
      @close="onClose"
      @open="onOpen"
  >
    <template #default="{open, close}">
      <!-- Prefix (search icon or spinner) -->
      <span v-if="!disableSearch && isOpen"
            class="input-group-text"
            @mouseup.stop="open"
      >
        <Spinner v-if="preloading"/>
        <i v-else
           class="icon-search"
        />
    </span>

      <!-- Search -->
      <slot
          v-if="isOpen && !disableSearch"
          name="search"
          v-bind="{request, open}"
      >
        <input
            ref="searchRef"
            type="text"
            :placeholder="$t('front.search')"
            class="form-control select__search"
            spellcheck="false"
            :class="[inputClass, { 'form-control-sm': small }]"
            :value="request.search"
            @change.stop="request.search = $event.target.value"
            @input="request.search = $event.target.value"
            @focus="open"
        />
      </slot>


      <!-- Output -->
      <div v-if="!isOpen || (isOpen && disableSearch)"
           class="select__output form-control"
           :class="[inputClass, { 'form-control-sm': small, 'select__output--single': !multiple }]"
           tabindex="0"
           @mouseup.stop="() => errorMessage ? null : open()"
      >
        <Spinner
            v-if="preloading"
            class="w-10"
        />

        <template v-if="errorMessage">
          <i class="icon-error text-danger" />
          <span  class="text-danger">{{ errorMessage }}</span>
        </template>

        <span v-else-if="!inputValues.length"
              class="input-placeholder"
        >
        {{ placeholder }}
      </span>

        <SelectOutput
            v-else
            :multiple="multiple"
            :values="inputValues"
            :options="allOptions"
            :value-key="valueKey"
            :title-key="titleKey"
            :can-remove="multiple && !request.select"
            :preloading="preloading"
            :pre-selection="request.preSelection"
            :quick-filter="quickFilter"
            @remove="onRemove"
        />
      </div>

      <!--- Arrow and clear --->
      <template v-if="!isOpen || disableSearch">
        <i v-if="showClear"
           class="icon-times input-group-text"
           :class="{disabled}"
           @mouseup.stop="clear"
        />
        <i v-else
           @mouseup.stop="open"
           class="select__arrow input-group-text action-icon select__arrow icon-chevron-down"
        />
      </template>
    </template>

    <!-- Dropdown options -->
    <template #content="{open, close}">
      <SelectOptions
          ref="optionsRef"
          :error="errorMessage"
          v-bind="optionsProps"
          @select="onSelect"
          @select-group="emit('selectGroup', $event)"
          @group-toggle="emit('groupToggle', $event)"
          @scroll-end="emit('scrollEnd', $event)"
          @page-change="emit('pageChange', $event)"
      >
        <template #error>
          <div class="alert alert-danger w-100 mb-0">
            {{ errorMessage }}
          </div>
        </template>
        <template #option="data">
          <slot name="option" v-bind="data" />
        </template>
        <template #emptyList="data">
          <slot name="emptyList" v-bind="data" />
        </template>
      </SelectOptions>

      <slot name="footer">
        <SelectFooter
            ref="footerRef"
            v-if="multiple"
            :multiple="multiple"
            :is-static="isStatic"
            :is-select="!!request.select"
            :all-selected="allSelected"
            :loading="loading"
            :initial-load="initialLoad"
            :request="request"
            @select="selectAll"
            @cancel="cancel"
            @apply="apply"
        />
      </slot>
    </template>
  </Dropdown>
</template>
<script setup>
import Spinner from "@/js/Elements/Spinner.vue";
import {computed, nextTick, ref, watch} from "vue";
import SelectOptions from "@/js/Elements/Select/SelectOptions.vue";
import SelectOutput from "@/js/Elements/Select/SelectOutput.vue";
import SelectFooter from "@/js/Elements/Select/SelectFooter.vue";
import ConfirmModal from "@/js/Components/Modal/ConfirmModal.vue";
import {useModal} from "@/js/Plugins/Modal.js";
import Dropdown from "@/js/Elements/Dropdown.vue";

const emit = defineEmits([
  'clear', 'undo', 'all', 'apply', 'load', 'remove', 'confirm', 'focus', 'blur',
  'select', 'selectGroup', 'groupToggle', 'scrollEnd', 'pageChange'
]);
const props = defineProps({
  inputValues: Array,
  inputClass: [String,Array,Object],
  inputGroupClass: [String,Array,Object],
  small: Boolean,
  disableSearch: Boolean,
  loading: Boolean,
  preloading: Boolean,
  disabled: Boolean,
  multiple: Boolean,
  clearable: Boolean,
  placeholder: String,
  request: Object,
  container: Object,
  optionsProps: Object,
  allOptions: Array,
  allSelected: Number,
  valueKey: String,
  titleKey: String,
  initialLoad: Boolean,
  isStatic: Boolean,
  confirmable: Boolean,
  autocomplete: Boolean,
  quickFilter: Boolean,
  placement: String,
  errorMessage: String,
  popperClass: [Array, String, Object]
});

defineExpose({ close, open });

const modal = useModal();
const isOpen = ref(false);
const searchRef = ref();
const optionsRef = ref();
const footerRef = ref();
const dropdownRef = ref();
const showClear = computed(() => {
  if (props.multiple || !props.clearable) return false;
  return props.inputValues.length;
});
let valuesClone = null;

watch(() => isOpen.value, (value) => {
  emit(value ? 'focus' : 'blur');
});

function open() {
  isOpen.value = true;
  if (dropdownRef.value) {
    dropdownRef.value.open();
  }
}

function close() {
  isOpen.value = false;
  if (dropdownRef.value) {
    dropdownRef.value.close();
  }
}

function cancel() {
  props.request.paginated && props.multiple ? emit('undo') : clear();
  close();
}

function onSelect(option, confirmed = false) {
  if (confirmed || !props.confirmable) {
    emit('select', option);
    return;
  }

  if (dropdownRef.value) {
    dropdownRef.value.close();
  }
  isOpen.value = false;
  modal.open(ConfirmModal, {
    id: 'select-confirmation',
    onConfirm() {
      onSelect(option, true);
      nextTick(() => {
        emit('confirm');
        modal.close('select-confirmation');
      });
    }
  });
}

function apply() {
  emit('apply');
  valuesClone = null;
  close();
}

function onRemove(value) {
  emit('remove', value);
}


function selectAll(value) {
  emit('all', value);
}

function onClose() {
  isOpen.value = false;
  document.removeEventListener('mouseup', clickOutside);

  if (valuesClone !== null || props.autocomplete || !props.multiple) {
    emit('apply', valuesClone);
  }
}

function onOpen() {
  // hideAllPoppers();
  if (!props.initialLoad && !props.autocomplete) {
    emit('load');
  }

  isOpen.value = true;
  valuesClone = props.multiple ? JSON.parse(JSON.stringify(props.inputValues)) : null;
  document.addEventListener('mouseup', clickOutside);

  nextTick(() => {
    if (!searchRef.value) return;
    searchRef.value.focus();
  })
}

function clear() {
  valuesClone = null;
  emit('clear');
}

function clickOutside(e) {
  const optionsContainer = optionsRef.value ? optionsRef.value.getContainer() : null;
  const footerContainer = footerRef.value ? footerRef.value.getContainer() : null;

  if (
      (optionsContainer && optionsContainer.contains(e.target))
      || (footerContainer && footerContainer.contains(e.target))
      || (props.container && props.container.contains(e.target))
  ) return;
  close();
}
</script>
<style lang="scss">
@import "@/css/functions";
@import "@/css/variables";

.v-popper--select {
  .v-popper__inner {
    min-width: toRem(100);

    > div {
      display: flex;
      flex-direction: column;
    }
  }

  &-multiple .v-popper__inner {
    min-width: toRem(300);
  }
}

.input-group--select > .select {
  justify-content: center;
}
</style>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.select__output {
  align-items: center;
}

.select__output--single > div,
.input-placeholder {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.input-group {
  cursor: pointer;
}

:deep(> .v-popper) {
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

:deep(.spinner) {
  width: auto;
  display: inline-flex;
}

.select__output {
  display: flex;
  height: toRem(32);
  gap: spacing(2);
  align-self: center;

  &-items {
    display: flex;
    gap: spacing(1);
    overflow: hidden;
    font-weight: $font-weight-medium;
  }

  .badge {
    background-color: themeColor('neutral-200');
    color: themeColor('text');

    &--count {
      background-color: themeColor('primary-200');
    }
  }
}
</style>